import { isNativeApp } from './platform'

/**
 * Explicit notification (POST_NOTIFICATIONS) permission flow for native apps.
 *
 * This is intentionally separate from the push token registration hook
 * (`use-push-notifications.ts` / `push-notification-service.ts`) so the OS
 * permission dialog fires right after sign-in, decoupled from FCM token sync.
 * Requesting the runtime permission does not require google-services.json;
 * token registration (handled by the push hook) only runs when push is
 * compiled in.
 */

export type NativeNotificationPermission = 'granted' | 'denied' | 'prompt' | 'unsupported'

const ASKED_KEY = 'native-notification-permission-asked'

function mapReceive(receive: string | undefined): NativeNotificationPermission {
  if (receive === 'granted') return 'granted'
  if (receive === 'denied') return 'denied'
  if (receive === 'prompt' || receive === 'prompt-with-rationale') return 'prompt'
  return 'unsupported'
}

/** Current notification permission without prompting. */
export async function getNativeNotificationPermission(): Promise<NativeNotificationPermission> {
  if (!isNativeApp()) return 'unsupported'
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    const status = await PushNotifications.checkPermissions()
    return mapReceive(status.receive)
  } catch {
    return 'unsupported'
  }
}

function markAsked(): void {
  try {
    localStorage.setItem(ASKED_KEY, '1')
  } catch {
    /* storage unavailable — non-fatal */
  }
}

/**
 * Trigger the OS notification permission dialog (Android 13+ POST_NOTIFICATIONS,
 * iOS alert authorization). When granted and push is compiled in, also kicks off
 * FCM/APNs registration so the token sync listener (in the push hook) fires.
 */
export async function requestNativeNotificationPermission(): Promise<NativeNotificationPermission> {
  if (!isNativeApp()) return 'unsupported'
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    let status = await PushNotifications.checkPermissions()
    if (status.receive === 'prompt' || status.receive === 'prompt-with-rationale') {
      markAsked()
      status = await PushNotifications.requestPermissions()
    } else {
      markAsked()
    }
    const result = mapReceive(status.receive)
    if (result === 'granted') {
      // Route through the shared service so the `registration` listener is armed
      // BEFORE register() and the token is synced to the backend — independent of
      // whether the push hook is currently mounted.
      const { registerForPush } = await import('../services/push-registration')
      void registerForPush({ reason: 'primer' })
    }
    return result
  } catch {
    return 'unsupported'
  }
}
